import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

// Taalneutraal en platform-neutraal gehouden (geen i18n-/date-fns-imports
// hier): dit component wordt 1:1 hergebruikt door zowel de browser
// (web/src/utils/invoiceEmailUtils.js, handmatige "Verstuur factuur"-flow)
// als de Deno Edge Function-runtime
// (supabase/functions/_shared/invoice-email.ts, automatische factuurmail)
// — één PDF-generator-implementatie, geen tweede die kan wegdriften.
// `labels`/`statusLabels`/`fmtDate` komen daarom altijd van de aanroeper:
// web via `pdfFactuurLabels.js` (i18next), Deno via
// `_shared/invoicePdfLabels.ts` (statische NL-strings, zelfde waarden).

function euro(val) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(val ?? 0);
}

function addDaysIso(iso, days) {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function calcDueDate(createdAt, termDays, fmtDate) {
  if (!createdAt) return '—';
  const days = parseInt(termDays) || 14;
  try { return fmtDate(addDaysIso(createdAt, days)); }
  catch { return '—'; }
}

const GROEN      = '#1d9e75';
const DONKERGROEN = '#1a5c3a';
const LICHTGROEN  = '#f0faf5';
const GRIJS       = '#6b7280';
const LICHTGRIJS  = '#f9f9f9';
const RAND        = '#e5e7eb';

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1a1a2e',
    padding: '36 44 56 44',
    lineHeight: 1.4,
  },

  // ── Header ──────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 0,
    paddingBottom: 16,
    borderBottom: `2 solid ${GROEN}`,
  },
  headerLeft:  { flex: 2 },
  headerRight: { flex: 1.8, alignItems: 'flex-end' },

  factuurTitle: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: GROEN,
    marginBottom: 8,
  },
  clientName:  { fontSize: 11, fontFamily: 'Helvetica-Bold', marginBottom: 2, marginTop: 20 },
  clientLine:  { fontSize: 9, color: GRIJS, marginBottom: 1 },

  logoImg: { width: 60, height: 60, objectFit: 'contain', marginBottom: 6 },

  infoLine: { fontSize: 9, textAlign: 'right', marginBottom: 3, color: '#555' },

  companyName: { fontSize: 10, fontFamily: 'Helvetica-Bold', textAlign: 'right', marginBottom: 2 },
  companyLine: { fontSize: 8.5, color: GRIJS, textAlign: 'right', marginBottom: 1 },

  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 99,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    alignSelf: 'flex-end',
    marginBottom: 6,
    marginTop: 4,
  },

  // ── Midden: betaalgegevens + factuurdetails ──────────────────
  midRow:   { flexDirection: 'row', marginBottom: 20, marginTop: 18 },
  betaalBox: {
    flex: 1.3,
    marginRight: 16,
    backgroundColor: LICHTGROEN,
    borderRadius: 6,
    padding: '10 12',
  },
  sectionTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: GROEN,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  betaalBedrag: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: DONKERGROEN, marginBottom: 2 },
  betaalVoor:   { fontSize: 7.5, color: GRIJS, marginTop: 8, marginBottom: 12 },
  betaalRow:    { flexDirection: 'row', marginBottom: 3 },
  betaalKey:    { width: 72, fontSize: 8, color: GRIJS },
  betaalVal:    { flex: 1, fontSize: 8 },

  detailsBox: { flex: 1, paddingTop: 0 },
  detailRow:  { flexDirection: 'row', marginBottom: 5 },
  detailKey:  { width: 100, fontSize: 8, color: GRIJS },
  detailVal:  { flex: 1, fontSize: 8 },

  // ── Tabel ───────────────────────────────────────────────────
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: DONKERGROEN,
    padding: '7 10',
    borderRadius: 4,
    marginBottom: 0,
  },
  thText: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  tableRow:     { flexDirection: 'row', padding: '7 10', borderBottom: `1 solid ${RAND}` },
  tableRowEven: { backgroundColor: LICHTGRIJS },

  colDesc:  { flex: 3.5 },
  colQty:   { width: 36, textAlign: 'right' },
  colPrice: { width: 52, textAlign: 'right' },
  colBtwPct:{ width: 30, textAlign: 'right' },
  colExcl:  { width: 56, textAlign: 'right' },
  colBtw:   { width: 48, textAlign: 'right' },
  colIncl:  { width: 60, textAlign: 'right' },

  tdText:  { fontSize: 9 },
  tdMuted: { fontSize: 9, color: GRIJS },

  // ── Onderin: BTW-spec + totalen ──────────────────────────────
  bottomRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16 },

  btwTable:  { flex: 1.2, marginRight: 20, alignSelf: 'flex-start' },
  btwHeader: { flexDirection: 'row', borderBottom: `1 solid ${RAND}`, paddingBottom: 4, marginBottom: 5 },
  btwTh:     { flex: 1, fontSize: 7.5, color: GRIJS, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: 'Helvetica-Bold' },
  btwRow:    { flexDirection: 'row' },
  btwCell:   { flex: 1, fontSize: 9 },

  totaalBlok: { flex: 1 },
  totaalRij:  { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  totaalLabel: { flex: 1, fontSize: 9, color: GRIJS },
  totaalValue: { fontSize: 9, textAlign: 'right' },
  totaalBoldLabel: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
  totaalBoldValue: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: GROEN },

  // ── Voetnoot ─────────────────────────────────────────────────
  footnote: {
    marginTop: 20,
    paddingTop: 8,
    borderTop: `1 solid ${RAND}`,
    fontSize: 8,
    color: GRIJS,
  },

  // ── Pagina-footer ────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 44,
    right: 44,
    borderTop: `1 solid ${RAND}`,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 7.5, color: GRIJS },
});

const STATUS_STYLE = {
  sent: { backgroundColor: '#eff6ff', color: '#2563eb' },
  paid: { backgroundColor: '#f0fdf4', color: '#16a34a' },
};

export default function FactuurDocument({ invoice, lines = [], company = {}, labels, statusLabels, fmtDate }) {
  const {
    invoice_number,
    subtotal,
    btw_amount,
    total_amount,
    status,
    created_at,
    client,
  } = invoice;

  const companyName     = company.company_name              || 'Fit En Sterker';
  const companyAddress  = company.company_address           || '';
  const companyPostcode = company.company_postcode          || '';
  const companyCity     = company.company_city              || '';
  const companyEmail    = company.company_email             || '';
  const companyKvk      = company.company_kvk               || '';
  const companyBtw      = company.company_btw               || '';
  const companyIban     = company.company_iban              || '';
  const companyLogo     = company.company_logo_url          || '';
  const termDays        = company.company_payment_term_days || '14';
  const defaultFooter   = company.invoice_footer            || '';
  const invoiceFooter = (() => {
    if (status === 'draft')        return company.invoice_footer_draft        || defaultFooter;
    if (status === 'sent')         return company.invoice_footer_sent         || defaultFooter;
    if (status === 'reminded')     return company.invoice_footer_reminded     || defaultFooter;
    if (status === 'final_notice') return company.invoice_footer_final_notice || defaultFooter;
    if (status === 'collections')  return company.invoice_footer_collections  || defaultFooter;
    return defaultFooter;
  })();

  const dueDate = calcDueDate(created_at, termDays, fmtDate);
  const toonStatusBadge = status === 'sent' || status === 'paid';
  const statusStijl = STATUS_STYLE[status] || {};
  const statusLabel = (statusLabels && statusLabels[status]) || status;

  const cityLine = [companyPostcode, companyCity].filter(Boolean).join(' ');

  // BTW-specificatie per tarief vanuit de regellijnen
  const btwSpec = {};
  lines.forEach(line => {
    const pct  = line.btw_percentage ?? 9;
    const excl = (line.amount ?? (line.quantity * line.unit_price)) || 0;
    const btw  = Math.round(excl * (pct / 100) * 100) / 100;
    if (!btwSpec[pct]) btwSpec[pct] = { grondslag: 0, btw: 0 };
    btwSpec[pct].grondslag += excl;
    btwSpec[pct].btw       += btw;
  });
  const btwTarieven = Object.keys(btwSpec).map(Number).sort((a, b) => a - b);

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* ── HEADER: 2 kolommen ─────────────────────────────── */}
        <View style={s.headerRow}>

          {/* Links: Factuur-titel + cliëntgegevens */}
          <View style={s.headerLeft}>
            <Text style={s.factuurTitle}>{labels.factuur}</Text>
            <Text style={s.clientName}>{client?.full_name || '—'}</Text>
            {client?.email && <Text style={s.clientLine}>{client.email}</Text>}
            {(client?.street || client?.house_number) && (
              <Text style={s.clientLine}>{[client.street, client.house_number].filter(Boolean).join(' ')}</Text>
            )}
            {(client?.postal_code || client?.city) && (
              <Text style={s.clientLine}>{[client.postal_code, client.city].filter(Boolean).join(' ')}</Text>
            )}
          </View>

          {/* Rechts: Logo + bedrijfsgegevens + status stempel */}
          <View style={s.headerRight}>
            {companyLogo ? <Image src={companyLogo} style={s.logoImg} /> : null}
            {companyBtw && <Text style={s.infoLine}>{labels.btwnr}  {companyBtw}</Text>}
            {companyKvk && <Text style={s.infoLine}>{labels.kvk}  {companyKvk}</Text>}
            {toonStatusBadge && (
              <View style={[s.statusBadge, statusStijl]}>
                <Text>{statusLabel}</Text>
              </View>
            )}
            <Text style={s.companyName}>{companyName}</Text>
            {companyAddress ? <Text style={s.companyLine}>{companyAddress}</Text> : null}
            {cityLine ? <Text style={s.companyLine}>{cityLine}</Text> : null}
            {companyEmail ? <Text style={s.companyLine}>{companyEmail}</Text> : null}
          </View>
        </View>

        {/* ── MIDDEN: Betaalgegevens + Factuurdetails ─────────── */}
        <View style={s.midRow}>

          {/* Links: Betaalgegevens */}
          <View style={s.betaalBox}>
            <Text style={s.sectionTitle}>{labels.betaalgegevens}</Text>
            <Text style={s.betaalBedrag}>{euro(total_amount)}</Text>
            <Text style={s.betaalVoor}>{labels.teBetalen} {labels.voor} {dueDate}</Text>
            {companyIban ? (
              <View style={s.betaalRow}>
                <Text style={s.betaalKey}>{labels.naarIban}</Text>
                <Text style={s.betaalVal}>{companyIban}</Text>
              </View>
            ) : null}
            <View style={s.betaalRow}>
              <Text style={s.betaalKey}>{labels.opNaamVan}</Text>
              <Text style={s.betaalVal}>{companyName}</Text>
            </View>
            <View style={s.betaalRow}>
              <Text style={s.betaalKey}>{labels.omschrijvingBet}</Text>
              <Text style={s.betaalVal}>{labels.factuur} {invoice_number}</Text>
            </View>
          </View>

          {/* Rechts: Factuurdetails */}
          <View style={s.detailsBox}>
            <Text style={s.sectionTitle}>{labels.factuurdetails}</Text>
            <View style={s.detailRow}>
              <Text style={s.detailKey}>{labels.factuurnummer}</Text>
              <Text style={s.detailVal}>{invoice_number}</Text>
            </View>
            <View style={s.detailRow}>
              <Text style={s.detailKey}>{labels.factuurdatumLabel}</Text>
              <Text style={s.detailVal}>{fmtDate(created_at)}</Text>
            </View>
            <View style={s.detailRow}>
              <Text style={s.detailKey}>{labels.betalingstermijn}</Text>
              <Text style={s.detailVal}>{termDays} {labels.dagen}</Text>
            </View>
            <View style={s.detailRow}>
              <Text style={s.detailKey}>{labels.klantnummer}</Text>
              <Text style={s.detailVal}>{client?.customer_number || (client?.id ? client.id.slice(0, 8) + '…' : '—')}</Text>
            </View>
          </View>
        </View>

        {/* ── REGELOVERZICHT TABEL ─────────────────────────────── */}
        <View style={s.tableHeader}>
          <Text style={[s.thText, s.colDesc]}>{labels.omschrijving}</Text>
          <Text style={[s.thText, s.colQty]}>{labels.aantal}</Text>
          <Text style={[s.thText, s.colPrice]}>{labels.stukprijs}</Text>
          <Text style={[s.thText, s.colBtwPct]}>{labels.colBtwPct}</Text>
          <Text style={[s.thText, s.colExcl]}>{labels.colExcl}</Text>
          <Text style={[s.thText, s.colBtw]}>{labels.colBtw}</Text>
          <Text style={[s.thText, s.colIncl]}>{labels.colIncl}</Text>
        </View>

        {lines.map((line, idx) => {
          const excl   = line.amount ?? (line.quantity * line.unit_price);
          const pct    = line.btw_percentage ?? 9;
          const btwAmt = Math.round(excl * (pct / 100) * 100) / 100;
          const incl   = excl + btwAmt;
          return (
            <View key={line.id ?? idx} style={[s.tableRow, idx % 2 === 1 ? s.tableRowEven : {}]}>
              <Text style={[s.tdText, s.colDesc]}>{line.description}</Text>
              <Text style={[s.tdText, s.colQty]}>{line.quantity}</Text>
              <Text style={[s.tdText, s.colPrice]}>{euro(line.unit_price)}</Text>
              <Text style={[s.tdMuted, s.colBtwPct]}>{pct}%</Text>
              <Text style={[s.tdText, s.colExcl]}>{euro(excl)}</Text>
              <Text style={[s.tdMuted, s.colBtw]}>{euro(btwAmt)}</Text>
              <Text style={[s.tdText, s.colIncl]}>{euro(incl)}</Text>
            </View>
          );
        })}

        {lines.length === 0 && (
          <View style={s.tableRow}>
            <Text style={[s.tdMuted, { flex: 1, textAlign: 'center' }]}>{labels.geenRegeldetails}</Text>
          </View>
        )}

        {/* ── ONDERIN: BTW-specificatie + totalen ─────────────── */}
        <View style={s.bottomRow}>

          {/* BTW-specificatie */}
          <View style={s.btwTable}>
            <Text style={[s.sectionTitle, { marginBottom: 6 }]}>{labels.btwSpecificatie}</Text>
            <View style={s.btwHeader}>
              <Text style={s.btwTh}>{labels.btwPercent}</Text>
              <Text style={s.btwTh}>{labels.grondslag}</Text>
              <Text style={s.btwTh}>{labels.bedrag}</Text>
            </View>
            {btwTarieven.map(pct => (
              <View key={pct} style={s.btwRow}>
                <Text style={s.btwCell}>{pct}%</Text>
                <Text style={s.btwCell}>{euro(btwSpec[pct].grondslag)}</Text>
                <Text style={s.btwCell}>{euro(btwSpec[pct].btw)}</Text>
              </View>
            ))}
            {btwTarieven.length === 0 && (
              <View style={s.btwRow}>
                <Text style={[s.btwCell, { color: GRIJS }]}>—</Text>
              </View>
            )}
          </View>

          {/* Totalen */}
          <View style={s.totaalBlok}>
            <View style={s.totaalRij}>
              <Text style={s.totaalLabel}>{labels.totaalExclBtw}</Text>
              <Text style={s.totaalValue}>{euro(subtotal)}</Text>
            </View>
            {btwTarieven.map(pct => (
              <View key={pct} style={s.totaalRij}>
                <Text style={s.totaalLabel}>{labels.btw} {pct}%</Text>
                <Text style={s.totaalValue}>{euro(btwSpec[pct].btw)}</Text>
              </View>
            ))}
            {btwTarieven.length === 0 && (
              <View style={s.totaalRij}>
                <Text style={s.totaalLabel}>{labels.btw}</Text>
                <Text style={s.totaalValue}>{euro(btw_amount)}</Text>
              </View>
            )}
            <View style={[s.totaalRij, { borderTopWidth: 1, borderTopColor: '#ccc', paddingTop: 4, marginTop: 4, marginBottom: 0 }]}>
              <Text style={s.totaalBoldLabel}>{labels.totaalInclBtw}</Text>
              <Text style={s.totaalBoldValue}>{euro(total_amount)}</Text>
            </View>
          </View>
        </View>

        {/* ── Factuurvoetnoot ──────────────────────────────────── */}
        {invoiceFooter ? (
          <View style={s.footnote}>
            <Text>{invoiceFooter}</Text>
          </View>
        ) : null}

        {/* ── Pagina-footer (fixed) ────────────────────────────── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            {companyName}{companyEmail ? ` · ${companyEmail}` : ''}
          </Text>
          <Text style={s.footerText}>{invoice_number}</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `${labels.pagina} ${pageNumber} ${labels.van2} ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
